
import { Component, Prop, Vue } from "vue-property-decorator";

@Component
export default class CountUpNumber extends Vue {
  @Prop({ required: true }) target: number;
  @Prop({ default: 1400 }) duration: number;

  displayValue = "";
  observer: IntersectionObserver | null = null;
  animationFrameId: number | null = null;
  hasAnimated = false;

  created(): void {
    this.displayValue = this.formatNumber(this.target);
  }

  mounted(): void {
    const prefersReducedMotion =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (prefersReducedMotion) return;

    this.displayValue = "0";

    if (!("IntersectionObserver" in window)) {
      this.animate();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.animate();
          this.observer?.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    this.observer.observe(this.$el);
  }

  beforeDestroy(): void {
    this.observer?.disconnect();
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
    }
  }

  formatNumber(value: number): string {
    return Math.round(value).toLocaleString("en-US");
  }

  easeOutCubic(progress: number): number {
    return 1 - Math.pow(1 - progress, 3);
  }

  animate(): void {
    if (this.hasAnimated) return;
    this.hasAnimated = true;

    const duration = Math.max(0, this.duration);
    if (duration === 0) {
      this.displayValue = this.formatNumber(this.target);
      return;
    }

    let startTime: number | null = null;

    const step = (timestamp: number) => {
      if (startTime === null) startTime = timestamp;

      const progress = Math.min((timestamp - startTime) / duration, 1);
      const value = this.target * this.easeOutCubic(progress);
      this.displayValue = this.formatNumber(value);

      if (progress < 1) {
        this.animationFrameId = requestAnimationFrame(step);
      } else {
        this.displayValue = this.formatNumber(this.target);
        this.animationFrameId = null;
      }
    };

    this.animationFrameId = requestAnimationFrame(step);
  }
}
